import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, Modal, ModalContent, ModalTitleInverse } from '@c2fo/liquidity';
import { EyeIcon } from '@c2fo/liquidity/icons';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useOfferDiscountAPRDisplay } from '@/enterprise/features/takerMarketTable/utils/useOfferDiscountAPRDisplay';
import getTakerMarketDivisionTitle from '@/utils/getTakerMarketDivisionTitle';
import getTakerMarketName from '@/utils/getTakerMarketName';
import usePricingTypeDisplay from '@/utils/usePricingTypeDisplay';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../Table';
import { useOfferBuilderContext } from './OfferBuilderContext';
import { useRateCalculations } from './useRateCalculations';
import useStableGroupByBuyer from './useStableGroupByBuyer';
import { allMarketsHaveRates, calculateBlendedRate } from './utils';

const BlendedRateDisplay = ({
  takerMarkets,
  withInfo = false,
}: {
  takerMarkets: TakerMarket[];
  withInfo?: boolean;
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { getEffectiveRate } = useRateCalculations();
  const buyers = useStableGroupByBuyer({ takerMarkets });
  const { getPricingTypeDisplay } = usePricingTypeDisplay();
  const { offerDiscountAPRDisplay } = useOfferDiscountAPRDisplay();
  const {
    state: { rateType: rateTypeFromContext },
  } = useOfferBuilderContext();
  const displayRateType = rateTypeFromContext ?? 'APR';

  // only display blended rate if taker markets and all takermarkets have defined rates
  if (takerMarkets.length === 0 || !allMarketsHaveRates(takerMarkets, getEffectiveRate)) {
    return <span>--</span>;
  }

  const blendedRate = calculateBlendedRate({
    items: takerMarkets.map((tm) => {
      const { rate, rateType } = getEffectiveRate(tm);

      return {
        dpeWeightedAvg: tm.eligibleDpeWeightedAvg,
        dpeWeightedSum: tm.eligibleDpeWeightedSum,
        invoiceAmount: tm.eligibleInvoiceAmount,
        isDiscount: rateType === 'DISC',
        rate: rate!, // we ensure all rates are defined above
      };
    }),
    rateType: displayRateType,
  });

  const rateTypeLabel = displayRateType === 'APR' ? t('core.apr') : t('core.discountAbbreviation');

  return (
    <>
      {withInfo && (
        <Modal className="overflow-hidden" onClose={() => setOpen(false)} open={open} size="sm">
          <ModalTitleInverse>{t('builder.viewRateDetails')}</ModalTitleInverse>
          <ModalContent className="space-y-4 pt-0">
            <div className="-mx-6 flex items-center justify-between gap-1 bg-canvas px-6 py-4">
              <div>
                <div className="font-medium">{t('builder.todaysBlendedRate')}</div>
              </div>
              <div className="text-right">
                <div className="font-medium uppercase">{offerDiscountAPRDisplay(blendedRate * 100, rateTypeLabel)}</div>
              </div>
            </div>
            <div className="overflow-hidden rounded border">
              <div className="overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('core.division')}</TableHead>
                      <TableHead className="text-right">{t('core.rate')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyers
                      .flatMap((buyer) => buyer.takerMarkets)
                      .map((takerMarket) => {
                        const tmRateInfo = getEffectiveRate(takerMarket);

                        return (
                          <TableRow key={takerMarket.id}>
                            <TableCell className="max-w-56 truncate">
                              <div className="max-w-56 truncate">
                                <div className="truncate text-sm font-medium">{getTakerMarketName(takerMarket)}</div>
                                <div
                                  className="truncate text-sm font-medium"
                                  title={getTakerMarketDivisionTitle(takerMarket)?.title}
                                >
                                  {getTakerMarketDivisionTitle(takerMarket)?.content}
                                </div>
                                <div className="flex items-center gap-1 text-sm text-text-secondary">
                                  <div>{getPricingTypeDisplay(takerMarket)}</div>
                                  <div className="h-[3px] w-[3px] rounded-full bg-text-secondary" />
                                  <div>{takerMarket.currency}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div>
                                <div className="text-sm font-medium uppercase">
                                  {tmRateInfo.rate && tmRateInfo.rateType
                                    ? offerDiscountAPRDisplay(tmRateInfo.rate, tmRateInfo.rateType)
                                    : '--'}
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </div>
          </ModalContent>
        </Modal>
      )}
      <span className="flex items-center gap-1 uppercase">
        {offerDiscountAPRDisplay(blendedRate * 100, rateTypeLabel)}*
        {withInfo && (
          <IconButton
            className="h-7 w-7 bg-transparent [&>svg]:h-5 [&>svg]:w-5"
            icon={EyeIcon}
            name={t('builder.viewRateDetails')}
            onClick={() => setOpen(true)}
            size="sm"
            variant="link"
          />
        )}
      </span>
    </>
  );
};

export default BlendedRateDisplay;
